import { type UpdateUserInput, UpdateUserInputSchema, type User } from '@sazeo/schemas';
import { getTenantClient } from '@sazeo/supabase-tenant';
import { type CoreError, coreError } from './errors';

/**
 * Fetch the current user's profile row from `public.users`.
 * RLS auto-filters to `auth.uid()`.
 */
export const getCurrentUser = async (): Promise<User | CoreError> => {
  const client = getTenantClient();
  const sessionRes = await client.auth.getSession();
  const userId = sessionRes.data.session?.user.id;
  if (!userId) return coreError('FORBIDDEN', 'No active session');

  const { data, error } = await client
    .from('users')
    .select('id, email, display_name, avatar_url, preferred_lang, created_at, updated_at')
    .eq('id', userId)
    .maybeSingle();

  if (error) return coreError('INTERNAL', error.message);
  if (!data) return coreError('NOT_FOUND', 'Profile row missing — signup hook failure');
  return data as User;
};

/**
 * Update mutable fields on the current user's profile (display name, avatar,
 * preferred language).
 */
export const updateCurrentUser = async (input: UpdateUserInput): Promise<User | CoreError> => {
  const parsed = UpdateUserInputSchema.safeParse(input);
  if (!parsed.success) {
    return coreError('VALIDATION', 'Invalid input', { issues: parsed.error.issues });
  }

  const client = getTenantClient();
  const sessionRes = await client.auth.getSession();
  const userId = sessionRes.data.session?.user.id;
  if (!userId) return coreError('FORBIDDEN', 'No active session');

  const { data, error } = await client
    .from('users')
    .update(parsed.data)
    .eq('id', userId)
    .select()
    .single();

  if (error) return coreError('INTERNAL', error.message);
  return data as User;
};

/**
 * Fetch a user profile by id. RLS enforces that the requester shares at
 * least one tenant membership with the target (or is the target themselves).
 */
export const getUserById = async (userId: string): Promise<User | CoreError> => {
  const client = getTenantClient();
  const { data, error } = await client
    .from('users')
    .select('id, email, display_name, avatar_url, preferred_lang, created_at, updated_at')
    .eq('id', userId)
    .maybeSingle();

  if (error) return coreError('INTERNAL', error.message);
  if (!data) return coreError('NOT_FOUND', 'User not found or no access');
  return data as User;
};
